import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, ElementRef, Input, ViewChild } from '@angular/core';
import {
   AbstractControl,
   ControlValueAccessor,
   FormArray,
   FormControl,
   NG_VALUE_ACCESSOR,
   UntypedFormControl,
   Validators,
} from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';
import { DeviceTypes } from 'generated/graphql-types';
import { DeviceOption } from 'src/app/pages/phones/models/device-option.interface';
import { addSpacesToTypeName } from 'src/app/pipes/split-enum-strings/add-spaces.pipe';
import { DeviceSelectComponent } from '../../../phone/component/device-select-component';
import { LcsSingleSelectSelectedEvent } from '../../../single-select/single-select-base';
import { DeviceChipInputOption } from './device-phone-number.model';

@Component({
    providers: [{ multi: true, provide: NG_VALUE_ACCESSOR, useExisting: DeviceChipInputComponent }],
    selector: 'ndt-device-chip-input',
    styleUrls: ['./device-chip-input.component.scss'],
    templateUrl: './device-chip-input.component.html',
    standalone: false
})
export class DeviceChipInputComponent implements ControlValueAccessor {
   @Input() required: boolean = false;

   @Input() formArray: FormArray;

   @Input() accountId: number;

   @ViewChild('deviceInput') deviceInput: ElementRef<HTMLInputElement>;

   @ViewChild('deviceSelect') autoComplete: DeviceSelectComponent;

   deviceChipsControl = new UntypedFormControl(Validators.required);

   deviceInputControl = new UntypedFormControl();

   values: DeviceChipInputOption[] = [];

   displayDevices: DeviceOption[] = [];

   DeviceTypes = DeviceTypes;

   get displayValues(): DeviceChipInputOption[] {
      return this.values.filter((value) => !value.isDeleted);
   }

   separatorKeysCodes: number[] = [ENTER, COMMA];

   onChange: (values: DeviceChipInputOption[]) => void;

   onTouched: () => void;

   writeValue(values: DeviceChipInputOption[]): void {
      if (values) {
         this.values = values;
      } else {
         this.values = [];
      }
      this.deviceChipsControl.setValue(values);
      this.removeErrors();
      this.updateDisplayDevices();
   }
   ngAfterViewInit(): void {
      this.values = this.formArray.controls.map((control: AbstractControl) => {
         const formControl = control as FormControl;
         const device = formControl.value.device;
         return {
            deviceId: formControl.value.deviceId,
            value: {
               phoneId: device.phoneId,
               macAddress: device.macAddress,
               description: device.description,
               deviceTypeId: device.deviceTypeId,
            } as DeviceOption,
            isDeleted: false,
         } as DeviceChipInputOption;
      });
      if (this.values.length > 0 && this.values[0].deviceId == null) {
         this.values = [];
      }
      this.deviceInputControl.setValue(null);
      this.deviceChipsControl.setValue(this.values);
      this.displayValues.forEach((value) => {
         if (value.deviceId && !value.value.deviceId) {
            value.value.deviceId = value.deviceId;
         }
      });
      this.updateDisplayDevices();
   }

   registerOnChange(onChange: (values: DeviceChipInputOption[]) => void): void {
      this.onChange = onChange;
   }

   registerOnTouched(onTouched: () => void): void {
      this.onTouched = onTouched;
   }

   removeValue(value: DeviceChipInputOption): void {
      const values: DeviceChipInputOption[] = [...this.values];
      const indexToRemove = values.findIndex((v) => {
         {
            return v.value === value.value && v.deviceId === value.deviceId;
         }
      });
      if (values[indexToRemove].deviceId === 0) {
         values.splice(indexToRemove, 1);
      } else {
         values[indexToRemove].isDeleted = true;
      }
      this.values = values;

      this.formArray.removeAt(indexToRemove);
      this.formArray.markAsDirty();
      this.updateValueAndControls(this.values);
   }

   add(event: MatChipInputEvent): void {
      // Add number only when autocomplete is not open
      if (this.autoComplete.isOpen) {
         const value = event.value;
         const trimmedValue = value.trim();

         const displayIds = this.displayValues.map((v) => v.value.deviceId);
         if (trimmedValue) {
            const deviceOptionComponent = this.autoComplete.options.find((o) => {
               return (
                  (o.value &&
                     o.value.deviceTypeId &&
                     o.value.deviceTypeId.toLowerCase() === trimmedValue.toLowerCase() &&
                     !displayIds.includes(o.value.deviceId)) ||
                  false
               );
            });
            if (deviceOptionComponent && deviceOptionComponent.value) {
               const existingValue = this.values.find((v) => v.value === deviceOptionComponent.value);
               if (!existingValue) {
                  const newValue: DeviceChipInputOption = {
                     deviceId: 0,
                     value: deviceOptionComponent.value,
                  };
                  this.values = [...this.values, newValue];
                  this.updateValueAndControls(this.values);
               }
            }
         }
      }
   }

   getChipDisplay(value: DeviceChipInputOption): string {
      const device = value;
      if (device.value.description && device.value.description.trim() !== '')
         return `${addSpacesToTypeName(device.value.deviceTypeId)} - ${device.value.macAddress} (${
            device.value.description
         })`;
      else {
         return `${addSpacesToTypeName(device.value.deviceTypeId)} - ${device.value.macAddress}`;
      }
   }

   selected(event: LcsSingleSelectSelectedEvent<DeviceOption>): void {
      if (event.option.value) {
         const newValue: DeviceChipInputOption = {
            deviceId: event.option.value.deviceId,
            value: event.option.value,
         };
         this.values = [...this.values, newValue];
         this.updateValueAndControls(this.values);
      }
   }

   removeErrors(): void {
      if (!this.deviceChipsControl.errors?.required) {
         this.deviceChipsControl.setErrors(null);
      }
   }

   updateDisplayDevices(): void {
      this.displayDevices = this.displayValues.map((dev) => {
         return dev.value as DeviceOption;
      });
   }

   updateValueAndControls(values: DeviceChipInputOption[]): void {
      const validValues = values.filter((value) => !value.isDeleted);

      const currentDeviceIds = new Set(
         this.formArray.controls.map((control: AbstractControl) => control.value.deviceId)
      );

      validValues.forEach((value) => {
         if (!currentDeviceIds.has(value.deviceId)) {
            this.formArray.push(new UntypedFormControl(value));
         }
      });

      this.values = validValues;

      if (this.autoComplete.panel) {
         this.autoComplete.panel.visible = false;
      }
      this.deviceInput.nativeElement.value = '';
      this.deviceInputControl.setValue(null);
      this.deviceChipsControl.setValue(this.displayValues);

      this.formArray.markAsDirty();
      if (this.onChange) {
         this.onChange(this.values);
      }
      this.removeErrors();
      this.updateDisplayDevices();
      if (this.deviceChipsControl.errors) {
         this.formArray.setErrors(this.deviceChipsControl.errors);
      }
   }
}
